/**
 * KodeVedic Design System
 * ────────────────────────────────────────────────────────────────
 * Single source of truth for all visual design decisions.
 * Import DS in any component to keep the site consistent.
 *
 * Usage:
 *   import { DS } from "../utils/designSystem"
 *   <div className={`${DS.card.base} ${DS.card.hover}`}>
 */

export const DS = {

  // ─── BRAND COLORS (reference only - use Tailwind tokens in className) ───
  palette: {
    yellow:          "#FFD60A",   // yellow-50
    cyanStart:       "#1FA2FF",   // highlight gradient start
    cyanMid:         "#12D8FA",   // highlight gradient mid
    cyanEnd:         "#A6FFCB",   // highlight gradient end
    bgDeep:          "#000814",   // richblack-900
    bgCard:          "#161D29",   // richblack-800
    bgHover:         "#2C333F",   // richblack-700
    textPrimary:     "#F1F2FF",   // richblack-5
    textSecondary:   "#838894",   // richblack-300
    textMuted:       "#585D69",   // richblack-500
    border:          "#2C333F",   // richblack-700
    green:           "#06D6A0",   // caribbeangreen-100
  },

  // ─── GRADIENTS ───────────────────────────────────────────────────────────
  gradient: {
    // Brand highlight - wrap text in this to get the cyan gradient
    highlight:    "bg-gradient-to-b from-[#1FA2FF] via-[#12D8FA] to-[#A6FFCB] text-transparent bg-clip-text",
    // Subtle hero background glow
    heroBlobTop:  "bg-gradient-to-bl from-[#1FA2FF]/10 via-[#12D8FA]/5 to-transparent",
    heroBlobBtm:  "bg-gradient-to-tr from-yellow-50/5 to-transparent",
    // Card glass shine
    cardShine:    "bg-gradient-to-b from-richblack-700/40 to-transparent",
  },

  // ─── TYPOGRAPHY ──────────────────────────────────────────────────────────
  text: {
    // Display / headings
    display:    "text-5xl lg:text-6xl font-bold leading-tight text-richblack-5",
    h1:         "text-4xl lg:text-5xl font-bold leading-tight text-richblack-5",
    h2:         "text-3xl lg:text-4xl font-semibold text-richblack-5",
    h3:         "text-xl lg:text-2xl font-semibold text-richblack-5",
    h4:         "text-lg font-semibold text-richblack-5",
    // Section chrome
    sectionTitle:   "text-3xl lg:text-4xl font-semibold text-richblack-5",
    sectionSub:     "text-base lg:text-lg text-richblack-300 leading-relaxed",
    // Body
    body:    "text-base text-richblack-300 leading-relaxed",
    bodySm:  "text-sm  text-richblack-300 leading-relaxed",
    label:   "text-sm  font-medium text-richblack-200",
    caption: "text-xs  text-richblack-500",
    // Muted
    muted:   "text-richblack-400",
  },

  // ─── LAYOUT / SPACING ────────────────────────────────────────────────────
  layout: {
    section:          "py-16 lg:py-24",
    sectionCompact:   "py-10 lg:py-16",
    container:        "w-11/12 max-w-maxContent mx-auto",
    containerNarrow:  "w-11/12 max-w-[900px] mx-auto",
    containerWide:    "w-11/12 max-w-[1400px] mx-auto",
  },

  // ─── CARD ─────────────────────────────────────────────────────────────────
  card: {
    base:    "bg-richblack-800 rounded-2xl border border-richblack-700",
    hover:   "hover:border-richblack-600 hover:shadow-[0_8px_32px_rgba(0,0,0,0.4)] transition-all duration-300",
    pad:     "p-6",
    padSm:   "p-4",
    // Highlighted variant (yellow accent border on hover)
    accent:  "bg-richblack-800 rounded-2xl border border-richblack-700 hover:border-yellow-50/30 transition-all duration-300",
    // Glassmorphism-lite
    glass:   "bg-richblack-800/60 backdrop-blur-sm rounded-2xl border border-richblack-700/60",
  },

  // ─── BUTTONS ─────────────────────────────────────────────────────────────
  btn: {
    // Filled yellow - primary CTA
    primary:
      "inline-flex items-center justify-center gap-2 bg-yellow-50 text-richblack-900 font-bold px-6 py-3 rounded-lg shadow-[2px_2px_0px_rgba(255,255,255,0.18)] hover:shadow-none hover:scale-95 transition-all duration-200",
    // Ghost dark - secondary CTA
    secondary:
      "inline-flex items-center justify-center gap-2 bg-richblack-800 border border-richblack-700 text-richblack-100 font-semibold px-6 py-3 rounded-lg hover:bg-richblack-700 hover:scale-95 transition-all duration-200",
    // Text-only
    ghost:
      "inline-flex items-center gap-2 text-richblack-200 font-medium px-4 py-2 rounded-lg hover:bg-richblack-800 transition-all duration-200",
    // Icon button
    icon:
      "p-2 rounded-full text-richblack-300 hover:bg-richblack-700 hover:text-richblack-5 transition-all duration-200",
  },

  // ─── BADGE / PILL ─────────────────────────────────────────────────────────
  badge: {
    base:    "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium",
    neutral: "bg-richblack-800 border border-richblack-700 text-richblack-300",
    success: "bg-caribbeangreen-900 border border-caribbeangreen-700 text-caribbeangreen-25",
    warning: "bg-yellow-900 border border-yellow-700 text-yellow-50",
    info:    "bg-blue-900 border border-blue-700 text-blue-25",
  },

  // ─── BORDERS ─────────────────────────────────────────────────────────────
  border: {
    base:     "border border-richblack-700",
    subtle:   "border border-richblack-800",
    accent:   "border border-yellow-50/20",
    divider:  "border-b border-richblack-700",
    dividerT: "border-t border-richblack-700",
  },

  // ─── SHADOWS ─────────────────────────────────────────────────────────────
  shadow: {
    card:      "shadow-[0_4px_24px_rgba(0,0,0,0.3)]",
    cardHover: "shadow-[0_8px_32px_rgba(0,0,0,0.5)]",
    glow:      "shadow-[0_0_24px_rgba(18,216,250,0.12)]",
    glowYellow:"shadow-[0_0_24px_rgba(255,214,10,0.12)]",
    code:      "shadow-[8px_8px_0_rgba(255,214,10,0.12)]",
    inner:     "shadow-inner shadow-richblack-900",
  },

  // ─── ANIMATION / TRANSITION ───────────────────────────────────────────────
  motion: {
    fast:  "transition-all duration-150",
    base:  "transition-all duration-200",
    slow:  "transition-all duration-300",
    enter: "transition-all duration-500 ease-out",
  },

  // ─── BORDER RADIUS ────────────────────────────────────────────────────────
  radius: {
    sm:   "rounded-lg",
    base: "rounded-xl",
    lg:   "rounded-2xl",
    xl:   "rounded-3xl",
    full: "rounded-full",
  },

  // ─── INPUT / FORM ─────────────────────────────────────────────────────────
  input: {
    base:
      "w-full bg-richblack-800 border border-richblack-700 text-richblack-5 placeholder:text-richblack-400 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-yellow-50/50 focus:ring-1 focus:ring-yellow-50/30 transition-all duration-200",
    label: "block text-sm font-medium text-richblack-200 mb-1.5",
    error: "text-xs text-pink-200 mt-1",
  },

  // ─── SECTION CHROME ───────────────────────────────────────────────────────
  // Reusable section heading block - pair title + optional subtitle
  section: {
    // Use these as a pattern:
    //   <div className={DS.section.header}>
    //     <h2 className={DS.text.sectionTitle}>Title</h2>
    //     <p className={DS.text.sectionSub}>Sub</p>
    //   </div>
    header:       "flex flex-col items-center text-center gap-3 mb-10 lg:mb-14",
    headerLeft:   "flex flex-col gap-3 mb-8",
    underline:    "mt-2 h-1 w-16 bg-yellow-50 rounded-full mx-auto",
    underlineLeft:"mt-2 h-1 w-16 bg-yellow-50 rounded-full",
  },
};

export default DS;
